import { inject } from "@angular/core";
import { type ActivatedRouteSnapshot, type ResolveFn, Router } from "@angular/router";

import { TranslateService } from "@ngx-translate/core";
import { MessageService } from "primeng/api";
import { catchError, EMPTY, map } from "rxjs";

import { APP_PATHS } from "@/app/shared/constants";
import { buildAbsoluteRoute } from "@/app/shared/utils/absolute-route.util";
import { WarehouseStore } from "../../warehouse.store";

/**
 * Validates the existence of a picking order before route activation.
 *
 * @param route Current route snapshot containing the `orderId` parameter.
 *
 * @returns Observable resolving to true if the order exists, otherwise redirects and returns EMPTY.
 */
export const pickingOrderResolver: ResolveFn<boolean> = (route: ActivatedRouteSnapshot) => {
  const store = inject(WarehouseStore);
  const router = inject(Router);
  const messageService = inject(MessageService);
  const translate = inject(TranslateService);

  const redirectToOrders = (): typeof EMPTY => {
    const url = buildAbsoluteRoute(APP_PATHS.orders);
    router.navigate([url]);
    return EMPTY;
  };

  const orderId = route.paramMap.get("orderId");

  if (!orderId) {
    return redirectToOrders();
  }

  return store.getOrderById(orderId).pipe(
    map(() => true),

    catchError(() => {
      messageService.add({
        severity: "error",
        summary: translate.instant("common.error"),
        detail: translate.instant("picking.error.retrieveOrder"),
      });

      return redirectToOrders();
    }),
  );
};
